import React, { useMemo } from "react";
import { Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import IconDefinition from "./IconDefinition";
import { useLastLiftMessage, useDebounceMap } from "../socketMessageState/BankMessagesContext";
import useDirectionDebounce from "./useDirectionDebounce";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import {
  usageToDoorIcon,
  usageHasDoorData,
  usageHasDirectionData,
} from "../../../utils/bitmaskToIcon";
import { stringToUuidT } from "../../../utils/uuid";
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";

interface LiftDegradedOverlayProps {
  lift: LiftInterface;
  totalFloors: number;
  liftIndex: number;
  liftCount: number;
  cellWidth: number;
}

/**
 * Semi-transparent overlay inside the hoistway column for a single lift.
 * Rendered when position is unknown — shows whatever state we DO have
 * (door icon, direction) centered vertically in the column.
 * Positioned absolutely within the hoistway container using lift index.
 */
function LiftDegradedOverlay({
  lift,
  totalFloors,
  liftIndex,
  liftCount,
  cellWidth,
}: LiftDegradedOverlayProps) {
  const liftUuid = useMemo(() => stringToUuidT(String(lift.id ?? "")), [lift.id]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const debounceMap = useDebounceMap();
  const { directionIcon, lastDirectionIcon } = useDirectionDebounce(
    socketMessageLift.usage,
    debounceMap,
  );

  const positionReceived = socketMessageLift.position !== null;
  const hasDoorData = usageHasDoorData(socketMessageLift.usage);
  const hasDirectionData = usageHasDirectionData(socketMessageLift.usage);

  if (positionReceived) return null;
  if (!hasDoorData && !hasDirectionData) return null;

  const doorIcon = usageToDoorIcon(socketMessageLift.usage, false);
  const displayDirection = directionIcon || lastDirectionIcon;
  const directionDimmed = !directionIcon && !!lastDirectionIcon;

  const cellHeight = 25;
  const totalHeight = totalFloors * cellHeight;
  const overlayHeight = 28;
  const overlayTop = Math.max(0, (totalHeight - overlayHeight) / 2);

  // Position within the hoistway using lift index and gap (6px gap between lifts)
  const gap = 6;
  const liftColumnLeft = liftIndex * (cellWidth + gap);
  const iconSize = Math.max(12, Math.min(cellWidth - 6, 20));

  return (
    <div
      data-testid="lift-degraded-overlay"
      aria-label={`Lift ${liftIndex + 1} of ${liftCount} in degraded state`}
      style={{
        position: "absolute",
        top: `${overlayTop}px`,
        left: `${liftColumnLeft}px`,
        width: `${cellWidth}px`,
        height: `${overlayHeight}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        zIndex: 10,
        pointerEvents: "none",
        opacity: 0.35,
        border: "1px solid rgba(255, 255, 255, 0.3)",
        borderRadius: "4px",
      }}
    >
      {doorIcon && (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType={LiftType[LiftType.ELEVATOR]}
          icon={doorIcon}
          iconSize={iconSize}
        />
      )}
      {displayDirection && (
        <span style={{ opacity: directionDimmed ? 0.5 : 1 }}>
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType={LiftType[LiftType.ELEVATOR]}
            icon={displayDirection}
            iconSize={iconSize}
          />
        </span>
      )}
    </div>
  );
}

export default React.memo(LiftDegradedOverlay);

/**
 * Warning icon shown next to lift name when data is incomplete.
 * Tooltip lists which data types are missing.
 */
export function LiftWarningIcon({ lift }: { lift: LiftInterface }) {
  const liftUuid = useMemo(() => stringToUuidT(String(lift.id ?? "")), [lift.id]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;

  const positionReceived = socketMessageLift.position !== null;
  const hasDoorData = usageHasDoorData(socketMessageLift.usage);
  const hasDirectionData = usageHasDirectionData(socketMessageLift.usage);

  if (positionReceived && hasDoorData && hasDirectionData) return null;

  const missing: string[] = [];
  if (!positionReceived) missing.push("position");
  if (!hasDoorData) missing.push("door status");
  if (!hasDirectionData) missing.push("direction");

  return (
    <Tooltip title={`Awaiting: ${missing.join(", ")}`} placement="bottom">
      <WarningOutlined
        data-testid="lift-warning-icon"
        style={{ color: "#faad14", fontSize: "10px", marginLeft: "3px" }}
      />
    </Tooltip>
  );
}
